/**
 * Shared shapes for Master Data → Sales & Targets.
 *
 * Mirrors the API's own types rather than re-deriving them, so a field renamed
 * server-side fails the build here instead of rendering as `undefined`.
 */

export type Scope = 'store' | 'employee';
export type Granularity = 'product' | 'sku';
export type SalesSource = 'upload' | 'running_stock' | 'detailed';
export type AppliesTo = 'both' | 'target' | 'sales';

export interface SalesTargetConfig {
  projectId: number;
  sourceScope: Scope;
  productGranularity: Granularity;
  applyDefaults: boolean;
  salesScope: Scope;
  salesSource: SalesSource;
  /**
   * An employee field that may identify a promoter in an uploaded sheet, beside
   * Employee Code. null = Employee Code only.
   */
  employeeKeyFieldId: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
}

/** An employee field offered as that identifier, with what decides whether it can be one. */
export interface EmployeeKeyField {
  id: string;
  label: string;
  fieldType: string;
  /** Promoters in the project holding a value for it. */
  withValue: number;
  blank: number;
  /** Values held by more than one promoter — each identifies nobody. */
  duplicateValues: number;
}

export interface ProductCoordinate {
  productId: string;
  skuId: string | null;
}

export interface CoordinateOption {
  coord: ProductCoordinate;
  label: string;
  aliases: string[];
}

/** Matches the API's `coordKey` exactly — the two must agree or nothing lines up. */
export const coordKey = (c: ProductCoordinate): string => `${c.productId}::${c.skuId ?? ''}`;

export interface TargetRow {
  id: string;
  periodMonth: string;
  scopeType: Scope;
  storeId: string | null;
  employeeCode: string | null;
  coord: ProductCoordinate;
  targetValue: number;
  source: 'manual' | 'upload';
  updatedBy: string | null;
  updatedAt: string | null;
}

export interface SalesRow {
  id: string;
  periodMonth: string;
  scopeType: Scope;
  storeId: string | null;
  employeeCode: string | null;
  coord: ProductCoordinate;
  soldValue: number;
  source: 'manual' | 'upload' | 'running_stock';
  customValues: Record<string, unknown>;
  updatedBy: string | null;
  updatedAt: string | null;
}

export interface MultiplierRule {
  id: string;
  scopeType: Scope;
  fieldSource: 'column' | 'custom_field';
  fieldKey: string;
  fieldLabel?: string;
  matchOp: string;
  matchValue: unknown;
  multiplier: number;
  appliesTo: AppliesTo;
  displayOrder: number;
  active: boolean;
}

export interface MultiplierField {
  source: 'column' | 'custom_field';
  key: string;
  label: string;
  kind: string;
  options: string[] | null;
}

export interface MultiplierPreviewRow {
  key: string;
  code: string;
  name: string | null;
  trade: string | null;
  category: string | null;
  city: string | null;
  multiplier: number;
  applied: { ruleId: string; fieldKey: string; multiplier: number }[];
}

export interface TargetDefault {
  id: string;
  scopeType: Scope;
  coord: ProductCoordinate;
  productLabel?: string;
  targetValue: number;
  fieldSource: 'column' | 'custom_field' | null;
  fieldKey: string | null;
  fieldLabel?: string;
  matchOp: string | null;
  matchValue: unknown;
  displayOrder: number;
  /**
   * Day of the month the default starts from; null = the whole month. From a later
   * day, earlier days carry none of it and the whole figure is spread over the
   * days from it.
   */
  activeFromDay: number | null;
}

/** The latest start day a default accepts — mirrors MAX_DEFAULT_START_DAY on the API. */
export const MAX_DEFAULT_START_DAY = 28;

export interface SalesField {
  id: string;
  label: string;
  fieldType: string;
  options: string[] | null;
  isRequired: boolean;
  displayOrder: number;
  active: boolean;
}

/**
 * A store or employee custom field a downloaded sheet can carry as a read-only
 * reference column. Which ones exist is the project's own Master Data.
 */
export interface ReferenceField {
  id: string;
  entity: 'store' | 'employee';
  label: string;
  fieldType: string;
}

export interface ComputedStoreLine {
  storeId: string;
  storeCode: string;
  storeName: string;
  coord: ProductCoordinate;
  productLabel: string;
  targetValue: number;
  origin: 'explicit' | 'default' | 'none';
  multiplier: number;
  perEmployeeTarget: number;
  peakHeadcount: number;
}

export interface ComputedEmployeeLine {
  employeeCode: string;
  employeeName: string | null;
  coord: ProductCoordinate;
  productLabel: string;
  monthlyTarget: number;
  rawMonthlyTarget: number;
  dayTarget: number;
  averageDayTarget: number;
  /**
   * Somebody whose target runs on past their last day: the days their pace is over,
   * worked and carried together. Absent otherwise, and from an older server.
   */
  paceDays?: number;
  deployedDays: number;
  /** Their last day, when they left the organization in this month. */
  leftOn?: string;
  contributions: {
    storeId: string; storeCode: string; storeName: string;
    /** Days actually worked here. */
    days: number;
    /** Days after they left that this store's target runs on to; `value` includes them. */
    carriedDays?: number;
    value: number; origin: string;
  }[];
}

export interface TargetWarning {
  kind: 'rounds_to_zero' | 'unstaffed' | 'no_multiplier_rule';
  storeId: string;
  storeCode: string;
  storeName: string;
  productLabel: string;
  storeMonthlyTarget: number;
  multiplier: number;
  message: string;
}

export interface CoverageGap {
  scope: Scope;
  key: string;
  code: string;
  name: string;
  coord: ProductCoordinate;
  productLabel: string;
  state: 'none' | 'default';
  defaultValue: number | null;
  /** The standing-in default's start day, when it does not cover the whole month. */
  defaultFromDay?: number | null;
}

export interface AchievementLine {
  employeeCode: string;
  employeeName: string | null;
  coord: ProductCoordinate;
  productLabel: string;
  target: number;
  sold: number;
  achievementPct: number | null;
  remaining: number;
}

export interface MonthComputation {
  month: string;
  referenceDay: string;
  totalDays: number;
  config: SalesTargetConfig;
  coordinates: CoordinateOption[];
  storeLines: ComputedStoreLine[];
  employeeLines: ComputedEmployeeLine[];
  warnings: TargetWarning[];
  unallocated: { storeId: string; storeCode: string; productLabel: string; value: number }[];
  coverage: {
    subjectsTotal: number;
    subjectsWithAnyTarget: number;
    linesExpected: number;
    linesExplicit: number;
    linesOnDefault: number;
    linesMissing: number;
    gaps: CoverageGap[];
    gapCount: number;
    /** Always every product, even when the view is filtered to one. */
    byProduct: {
      productId: string;
      skuId: string | null;
      productLabel: string;
      expected: number;
      explicit: number;
      onDefault: number;
      missing: number;
    }[];
  };
  totals: { storeTargetTotal: number; employeeTargetTotal: number; unallocatedTotal: number };
  truncated: boolean;
  achievement?: AchievementLine[];
  achievementTotals?: {
    targetTotal: number; soldTotal: number; achievementPct: number | null;
    employeesOnTarget: number; employeesBehind: number;
  };
  sales?: { source: SalesSource; scope: Scope; unattributed: unknown[] } | null;
}

export const MATCH_OP_LABELS: Record<string, string> = {
  eq: 'is',
  neq: 'is not',
  in: 'is any of',
  not_in: 'is none of',
  gt: 'is more than',
  gte: 'is at least',
  lt: 'is less than',
  lte: 'is at most',
  between: 'is between',
  blank: 'is blank',
  not_blank: 'has any value',
};

/** Operators that take no value, so the value input is hidden for them. */
export const VALUELESS_OPS = new Set(['blank', 'not_blank']);

/** Operators that compare numerically — offered only for numeric fields. */
export const NUMERIC_OPS = ['gt', 'gte', 'lt', 'lte', 'between'];

export const currentMonth = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/** The month just gone — the one a sales upload almost always means. */
export const previousMonth = (): string => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export const formatNumber = (n: number | null | undefined): string =>
  n === null || n === undefined || Number.isNaN(n) ? '—' : n.toLocaleString();

/** 1 -> "1st", 2 -> "2nd", 11 -> "11th", 22 -> "22nd". */
export const ordinalDay = (day: number): string => {
  const teen = day % 100 >= 11 && day % 100 <= 13;
  const suffix = teen ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[day % 10] ?? 'th';
  return `${day}${suffix}`;
};

// ─── Incentive ───────────────────────────────────────────────────────────────

export type IncentiveRuleType = 'date_gap' | 'field_match' | 'min_achievement';

/**
 * How a plan pays. 'grid' = a fixed amount per product by % of target, each product
 * judged on its own. 'per_unit' = a rupee rate for every unit sold, all counted
 * products together, rising in steps. Chosen when the plan is made; never changed.
 */
export type IncentivePlanKind = 'grid' | 'per_unit';

/** One step of a per-unit ladder: from this unit on, each unit pays `rate`. */
export interface IncentiveUnitRate {
  fromUnit: number;
  rate: number;
}

export interface IncentiveBand {
  id: string;
  label: string;
  /** Inclusive. */
  fromPct: number;
  /** EXCLUSIVE; null = the open-ended top row. See incentive.calc.ts — this is
   *  deliberately the opposite of the scorecard's slab convention. */
  toPct: number | null;
  displayOrder: number;
}

export interface IncentivePlanProduct {
  id: string;
  coord: ProductCoordinate;
  label: string;
  weightagePct: number;
  maxPotential: number;
}

export interface IncentiveRule {
  id: string;
  ruleType: IncentiveRuleType;
  coord: ProductCoordinate | null;
  fromFieldId: string | null;
  toFieldId: string | null;
  minDays: number | null;
  maxDays: number | null;
  fieldId: string | null;
  matchOp: string | null;
  matchValue: unknown;
  minAchievementPct: number | null;
  displayOrder: number;
  active: boolean;
}

export interface IncentivePlan {
  id: string;
  name: string;
  description: string | null;
  scopeType: Scope;
  fieldSource: 'column' | 'custom_field' | null;
  fieldKey: string | null;
  fieldLabel?: string | null;
  matchOp: string | null;
  matchValue: unknown;
  newJoinerMonths: number | null;
  newJoinerAmount: number | null;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  displayOrder: number;
  active: boolean;
  bands: IncentiveBand[];
  products: IncentivePlanProduct[];
  amounts: { bandId: string; productId: string; skuId: string | null; amount: number }[];
  rules: IncentiveRule[];
  /** Missing = 'grid', so a response from before per-unit plans still reads. */
  planKind?: IncentivePlanKind;
  /**
   * Per-unit plans only. "Pays from N units"; null = from the first unit. For a
   * per-unit plan, `products` lists the products counted when unitAllProducts is
   * false (weightage and max potential 0) and is empty when it is true; bands and
   * amounts are always empty.
   */
  unitMinUnits?: number | null;
  /** true = every product counts, including ones added later. */
  unitAllProducts?: boolean;
  /** Sorted by fromUnit; the first starts at unit 1. */
  unitRates?: IncentiveUnitRate[];
}

export const isPerUnitPlan = (p: Pick<IncentivePlan, 'planKind'>): boolean => p.planKind === 'per_unit';

export interface ProductPayout {
  coord: ProductCoordinate;
  label: string;
  target: number;
  sold: number;
  qualifyingSold: number;
  casesTotal: number;
  casesExcluded: number;
  achievementPct: number | null;
  bandLabel: string | null;
  amount: number;
  /**
   * Why it paid what it did, decided by the engine. The fields below are optional
   * only so an older response still renders — IncentivePayout falls back to the rest.
   */
  outcome?: 'paid' | 'below_minimum' | 'band_pays_nothing' | 'no_band' | 'no_target'
    /** Per-unit plans: this product's units went into the count; the line pays 0 itself. */
    | 'counted';
  /** Share of sales that passed the sale rules; null when none apply or nothing sold. */
  passRatePct?: number | null;
  /** The minimum a plan rule requires for this product; null when none. */
  minimumPct?: number | null;
  /** What that minimum reads: the pass rate where a sale rule applies, else achievement. */
  minimumOn?: 'pass_rate' | 'achievement' | null;
  note: string | null;
}

/** One step of the ladder as it paid somebody. Unreached steps have units 0. */
export interface PerUnitTierPayout {
  fromUnit: number;
  /** Inclusive; null = the last step, no cap. */
  toUnit: number | null;
  rate: number;
  units: number;
  amount: number;
}

/** How a per-unit plan paid one person, decided by the engine. */
export interface PerUnitPayout {
  /** Everything sold of the counted products. */
  soldUnits: number;
  /** Units counted, after the sale rules. */
  units: number;
  /** Sold but failed the sale rules. */
  excludedUnits: number;
  /** "Pays from"; null = from the first unit. */
  minUnits: number | null;
  reachedMinimum: boolean;
  /** The whole ladder, reached or not. */
  tiers: PerUnitTierPayout[];
  /** What the ladder pays — the row's `earned`. */
  amount: number;
  outcome: 'paid' | 'below_minimum' | 'nothing_counted' | 'no_rates' | 'rates_pay_nothing';
  /** The sum in one sentence, e.g. "24 units: 22 × ₹50 + 2 × ₹75 = ₹1,250". */
  working: string;
}

/** One store somebody worked at in the month, and the plan its days were paid under. */
export interface IncentiveMonthStore {
  storeId: string;
  storeCode: string | null;
  storeName: string | null;
  days: number;
  /** null = no plan covers this store, so its days earn nothing. */
  planId: string | null;
  planName: string | null;
}

/**
 * One plan's share of a month split between plans. earned/topUp/total, products and
 * perUnit are what the plan works out for the whole month; `paid` is what this part
 * adds to the row (about share × total), and the parts' `paid` add up to the row's total.
 */
export interface IncentivePlanPart {
  /** null = days at stores no plan covers. */
  planId: string | null;
  planName: string | null;
  days: number;
  share: number;
  earned: number;
  topUp: number;
  total: number;
  earnedBeforeMultiplier?: number;
  /** A new joiner under this plan's window. */
  newJoiner?: boolean;
  /** They left in this month, so this plan's guarantee does not apply. */
  guaranteeLost?: boolean;
  paid: number;
  paidTopUp: number;
  products: ProductPayout[];
  perUnit?: PerUnitPayout;
  notes: string[];
}

export interface IncentiveRunRow {
  employeeCode: string;
  employeeName: string | null;
  /** The store of the month they worked at most. */
  storeCode: string | null;
  storeName: string | null;
  /**
   * Every store of the month, most days first. Optional only so a response from a
   * server older than it still renders — the Store column then falls back to storeCode.
   */
  monthStores?: IncentiveMonthStore[];
  /**
   * Only when the month's days fall under more than one plan (or some under none).
   * planName, products and perUnit are then the main part's; earned/topUp/total are
   * the parts paid by their days.
   */
  planParts?: IncentivePlanPart[];
  /** false for somebody who worked the month and is no longer deployed. */
  stillDeployed?: boolean;
  /** Their last day deployed in the project, once they have gone. */
  deployedUntil?: string | null;
  planId: string;
  planName: string;
  newJoiner: boolean;
  /** A new joiner who left in this month, so the guarantee does not apply. */
  guaranteeLost?: boolean;
  tenureMonths: number | null;
  products: ProductPayout[];
  /** Present only on a row paid under a per-unit plan. */
  perUnit?: PerUnitPayout;
  /** What the plan paid — the grid, or the per-unit ladder. */
  earned: number;
  /** What is added on top to meet a new joiner's guaranteed minimum. */
  topUp: number;
  /** What is paid: earned + topUp. */
  total: number;
  earns: boolean;
  notes: string[];
  /**
   * Present only when a designation multiplier is in force for the month (×1
   * included). `earned` is then already multiplied; `earnedBeforeMultiplier` is
   * what the plan paid. designation is the 1Office budget name; null = none known.
   */
  designation?: string | null;
  multiplier?: number;
  earnedBeforeMultiplier?: number;
}

export interface IncentiveRun {
  month: string;
  uncovered: { employeeCode: string; employeeName: string | null }[];
  rows: IncentiveRunRow[];
  totals: {
    payees: number; total: number; earned: number; topUp: number;
    newJoiners: number; toppedUp: number;
  };
  hasCaseDetail: boolean;
  plansUsed: { id: string; name: string; payees: number; total: number; topUp: number }[];
  /** The designation multiplier in force for the month; absent when none is. */
  designationMultiplier?: { effectiveFrom: string; rows: DesignationMultiplierRow[] };
}

/** One designation's multiplier, e.g. Senior iPro ×1.3. */
export interface DesignationMultiplierRow {
  designation: string;
  multiplier: number;
}

/** A version of the project's designation multiplier: it applies from its month until a later one starts. */
export interface DesignationMultiplierVersion {
  /** 'YYYY-MM'. */
  effectiveFrom: string;
  rows: DesignationMultiplierRow[];
  savedBy: string | null;
  savedAt: string | null;
}

/** The version in force for `month` ('YYYY-MM'): the latest starting in it or before. */
export const multiplierVersionFor = (
  versions: DesignationMultiplierVersion[], month: string,
): DesignationMultiplierVersion | null =>
  [...versions].filter((v) => v.effectiveFrom <= month)
    .sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom))[0] ?? null;

/** "×1.0", "×1.3", "×1.25". */
export const formatMultiplier = (m: number): string =>
  `×${m.toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 2 })}`;

/** 'Aug 2026' for '2026-08'. */
export const shortMonth = (month: string): string => {
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const m = Number(month.slice(5, 7));
  return m >= 1 && m <= 12 ? `${names[m - 1]} ${month.slice(0, 4)}` : month;
};

export const RULE_TYPE_LABELS: Record<IncentiveRuleType, string> = {
  date_gap: 'Days between two dates',
  field_match: 'A sales detail matches',
  min_achievement: 'Minimum achievement',
};

/**
 * "ST-1 (26d), ST-2 (4d)": the stores behind a figure, with their days. One
 * formatter for the Results tab and the Incentive payout, so the two read the same.
 */
export const formatStoreDays = (stores: { storeCode: string | null; days: number }[]): string =>
  stores.map((s) => `${s.storeCode || '?'} (${s.days}d)`).join(', ');

/** Currency, whole rupees — incentive amounts are always rounded to units. */
export const formatMoney = (n: number | null | undefined): string =>
  n === null || n === undefined || Number.isNaN(n) ? '—' : `₹${Math.round(n).toLocaleString('en-IN')}`;
